/**
 * LabeledImageUpload Component
 * Uploads images one at a time with custom labels/names
 * Allows users to identify each image's purpose (e.g., "Sheet 3", "Detail A")
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { X, Upload, Edit2, Check } from 'lucide-react';
import SupabaseStorageService from '../../services/supabaseStorageService';
import FilePreviewModalSimple from '../FileUpload/FilePreviewModalSimple';

const LabeledImageUpload = ({ images = [], onImagesChange, projectId, clientId, disabled = false, type = 'project', hideUploadBox = false }) => {
  const [currentImage, setCurrentImage] = useState(null); // Currently uploaded image awaiting label
  const [currentLabel, setCurrentLabel] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editLabel, setEditLabel] = useState('');
  const [previewImage, setPreviewImage] = useState(null);
  const pasteAreaRef = useRef(null);
  const labelInputRef = useRef(null);

  // Auto-focus label input when image is uploaded
  useEffect(() => {
    if (currentImage && labelInputRef.current) {
      labelInputRef.current.focus();
    }
  }, [currentImage]);

  const uploadSingleImage = useCallback(async (file) => {
    if (!projectId || !clientId) {
      console.error('Missing projectId or clientId for upload');
      setError('Missing project or client information');
      return;
    }

    setUploading(true);
    setError(null);
    setUploadProgress(0);

    try {
      const onProgress = (progress) => {
        setUploadProgress(progress);
      };

      // Upload to appropriate folder based on type
      const result = await SupabaseStorageService.uploadFile(
        file,
        projectId,
        clientId,
        onProgress,
        null,
        type
      );

      if (result.error) {
        throw new Error(result.error);
      }

      // Set current image to await labeling
      setCurrentImage({ ...result, label: '' });
      setCurrentLabel('');
      setUploadProgress(0);
    } catch (error) {
      console.error('Error uploading image:', error);
      setError(error.message);
    } finally {
      setUploading(false);
    }
  }, [projectId, clientId, type]);

  // Handle paste event
  useEffect(() => {
    const handlePaste = async (e) => {
      if (disabled || uploading || currentImage) return;

      const items = e.clipboardData?.items;
      if (!items) return;

      // Only handle first image
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.indexOf('image') !== -1) {
          const file = items[i].getAsFile();
          if (file) {
            e.preventDefault();
            await uploadSingleImage(file);
            break; // Only upload first image
          }
        }
      }
    };

    const element = pasteAreaRef.current;
    if (element) {
      element.addEventListener('paste', handlePaste);
      return () => element.removeEventListener('paste', handlePaste);
    }
  }, [disabled, uploading, currentImage, uploadSingleImage]);

  // Handle file input
  const handleFileInput = async (e) => {
    const file = e.target.files?.[0];
    if (file && !currentImage && !uploading) {
      await uploadSingleImage(file);
    }
    // Reset input
    e.target.value = '';
  };

  const handleAddLabel = () => {
    if (!currentImage) return;

    const labelToUse = currentLabel.trim() || currentImage.filename;

    // Add to images list with label
    const newImage = { ...currentImage, label: labelToUse };
    const updatedImages = [...images, newImage];
    onImagesChange(updatedImages);

    // Reset for next image
    setCurrentImage(null);
    setCurrentLabel('');
  };

  const handleEditLabel = (index) => {
    setEditingIndex(index);
    setEditLabel(images[index].label || images[index].filename);
  };

  const handleSaveEditLabel = (index) => {
    const updatedImages = [...images];
    updatedImages[index] = {
      ...updatedImages[index],
      label: editLabel.trim() || updatedImages[index].filename
    };
    onImagesChange(updatedImages);
    setEditingIndex(null);
    setEditLabel('');
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setEditLabel('');
  };

  const removeImage = (index) => {
    const updatedImages = images.filter((_, i) => i !== index);
    onImagesChange(updatedImages);
  };

  const handleCancelCurrentUpload = () => {
    setCurrentImage(null);
    setCurrentLabel('');
    setError(null);
  };

  return (
    <div className="labeled-image-upload-container">
      {/* Current Image Labeling Area */}
      {currentImage && (
        <div style={{
          border: '2px solid var(--status-success)',
          padding: '20px',
          borderRadius: '4px',
          backgroundColor: 'var(--surface-secondary)',
          marginBottom: '20px'
        }}>
          <h4 style={{ margin: '0 0 15px 0', fontSize: '16px', color: 'var(--status-success)' }}>
            Add Sheet/Page Reference
          </h4>

          {/* Image Preview */}
          <div style={{ marginBottom: '15px' }}>
            <img
              src={currentImage.url}
              alt="Preview"
              style={{
                maxWidth: '300px',
                maxHeight: '200px',
                objectFit: 'contain',
                border: '1px solid var(--border-secondary)',
                backgroundColor: 'var(--surface-tertiary)',
                cursor: 'pointer'
              }}
              onClick={() => setPreviewImage(currentImage)}
            />
          </div>

          {/* Label Input */}
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '14px', color: 'var(--text-primary)' }}>
              Sheet/Page Number (Optional):
            </label>
            <input
              ref={labelInputRef}
              type="text"
              value={currentLabel}
              onChange={(e) => setCurrentLabel(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleAddLabel();
                }
              }}
              placeholder="e.g., Sheet 3, Page 12, C-101"
              style={{
                width: '100%',
                padding: '10px',
                fontSize: '14px',
                border: '1px solid var(--border-secondary)',
                backgroundColor: 'var(--surface-tertiary)',
                color: 'var(--text-primary)',
                borderRadius: '4px'
              }}
            />
            <p style={{ margin: '5px 0 0 0', fontSize: '12px', color: 'var(--text-secondary)' }}>
              Add a reference to identify which plan sheet/page this is from. File keeps its original name: {currentImage.filename}
            </p>
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={handleAddLabel}
              style={{
                padding: '10px 25px',
                fontSize: '14px',
                border: '1px solid var(--status-success)',
                backgroundColor: 'var(--status-success)',
                color: '#fff',
                cursor: 'pointer',
                fontWeight: 'bold',
                borderRadius: '4px'
              }}
            >
              Add to List
            </button>
            <button
              onClick={handleCancelCurrentUpload}
              style={{
                padding: '10px 25px',
                fontSize: '14px',
                border: '1px solid var(--border-primary)',
                backgroundColor: 'var(--surface-tertiary)',
                color: 'var(--text-primary)',
                cursor: 'pointer',
                borderRadius: '4px'
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Upload Area (only show when not labeling and not hidden) */}
      {!currentImage && !hideUploadBox && (
        <div style={{ marginBottom: '20px' }}>
          {/* Paste Zone */}
          <div
            ref={pasteAreaRef}
            className="image-paste-zone"
            tabIndex={0}
            style={{
              border: '2px dashed var(--border-secondary)',
              padding: '30px',
              textAlign: 'center',
              backgroundColor: disabled ? 'var(--surface-tertiary)' : 'var(--surface-secondary)',
              cursor: disabled ? 'not-allowed' : 'pointer',
              minHeight: '120px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '4px'
            }}
          >
            <Upload size={32} style={{ marginBottom: '10px', color: 'var(--text-secondary)' }} />
            <p style={{ margin: '0 0 10px 0', color: 'var(--text-secondary)', fontSize: '16px', fontWeight: 'bold' }}>
              {disabled ? 'Upload disabled' : 'Upload an Image'}
            </p>
            <p style={{ margin: '0 0 15px 0', color: 'var(--text-secondary)', fontSize: '14px' }}>
              Click here and press Ctrl+V to paste, or click button below
            </p>

            {/* File Input Button */}
            <input
              type="file"
              accept="image/*"
              onChange={handleFileInput}
              disabled={disabled || uploading}
              style={{ display: 'none' }}
              id="file-input-labeled"
            />
            <label
              htmlFor="file-input-labeled"
              style={{
                padding: '10px 25px',
                fontSize: '14px',
                border: '1px solid var(--border-primary)',
                backgroundColor: 'var(--surface-tertiary)',
                color: 'var(--text-primary)',
                cursor: disabled || uploading ? 'not-allowed' : 'pointer',
                borderRadius: '4px',
                display: 'inline-block'
              }}
            >
              Choose File
            </label>

            {uploading && (
              <div style={{ marginTop: '15px', width: '100%', maxWidth: '300px' }}>
                <p style={{ margin: '0 0 10px 0', color: 'var(--text-secondary)', fontSize: '14px' }}>
                  Uploading... {uploadProgress}%
                </p>
                <div style={{
                  height: '6px',
                  backgroundColor: 'var(--surface-hover)',
                  borderRadius: '3px',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    height: '100%',
                    width: `${uploadProgress}%`,
                    backgroundColor: 'var(--status-success)',
                    transition: 'width 0.3s'
                  }} />
                </div>
              </div>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div style={{
              marginTop: '10px',
              padding: '10px',
              backgroundColor: 'var(--status-error-bg)',
              border: '1px solid var(--status-error)',
              borderRadius: '4px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <span style={{ fontSize: '14px', color: 'var(--status-error)' }}>
                Error: {error}
              </span>
              <button
                onClick={() => setError(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0',
                  display: 'flex'
                }}
              >
                <X size={16} />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Uploaded Images List */}
      {images.length > 0 && (
        <div style={{ marginTop: '20px' }}>
          <h4 style={{ margin: '0 0 15px 0', fontSize: '16px', color: 'var(--text-primary)' }}>
            Uploaded Images ({images.length})
          </h4>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '15px'
          }}>
            {images.map((image, index) => (
              <div
                key={image.id}
                style={{
                  border: '1px solid var(--border-secondary)',
                  padding: '15px',
                  borderRadius: '4px',
                  backgroundColor: 'var(--surface-secondary)',
                  display: 'flex',
                  gap: '15px',
                  alignItems: 'flex-start'
                }}
              >
                {/* Thumbnail */}
                <img
                  src={image.url}
                  alt={image.label || image.filename}
                  style={{
                    width: '120px',
                    height: '90px',
                    objectFit: 'cover',
                    border: '1px solid var(--border-secondary)',
                    flexShrink: 0,
                    cursor: 'pointer'
                  }}
                  onClick={() => setPreviewImage(image)}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />

                {/* Label/Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  {editingIndex === index ? (
                    <div>
                      <input
                        type="text"
                        value={editLabel}
                        onChange={(e) => setEditLabel(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            handleSaveEditLabel(index);
                          } else if (e.key === 'Escape') {
                            handleCancelEdit();
                          }
                        }}
                        style={{
                          width: '100%',
                          padding: '8px',
                          fontSize: '14px',
                          border: '1px solid var(--status-success)',
                          backgroundColor: 'var(--surface-tertiary)',
                          color: 'var(--text-primary)',
                          borderRadius: '4px',
                          marginBottom: '8px'
                        }}
                        autoFocus
                      />
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          onClick={() => handleSaveEditLabel(index)}
                          style={{
                            padding: '6px 15px',
                            fontSize: '13px',
                            border: '1px solid var(--status-success)',
                            backgroundColor: 'var(--status-success)',
                            color: '#fff',
                            cursor: 'pointer',
                            borderRadius: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px'
                          }}
                        >
                          <Check size={14} />
                          Save
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          style={{
                            padding: '6px 15px',
                            fontSize: '13px',
                            border: '1px solid var(--border-primary)',
                            backgroundColor: 'var(--surface-tertiary)',
                            color: 'var(--text-primary)',
                            cursor: 'pointer',
                            borderRadius: '4px'
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '5px' }}>
                        <p style={{
                          margin: 0,
                          fontSize: '15px',
                          fontWeight: 'bold',
                          color: 'var(--text-primary)'
                        }}>
                          {image.label ? `${image.label}` : image.filename}
                        </p>
                        <button
                          onClick={() => handleEditLabel(index)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '4px',
                            display: 'flex',
                            color: 'var(--text-secondary)'
                          }}
                          title="Edit sheet/page reference"
                        >
                          <Edit2 size={14} />
                        </button>
                      </div>
                      {image.label && (
                        <p style={{
                          margin: 0,
                          fontSize: '12px',
                          color: 'var(--text-secondary)'
                        }}>
                          Original file: {image.filename}
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* Remove Button */}
                <button
                  onClick={() => removeImage(index)}
                  style={{
                    background: 'var(--surface-tertiary)',
                    border: '1px solid var(--border-secondary)',
                    color: 'var(--text-primary)',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    padding: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}
                  title="Remove image"
                >
                  <X size={16} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Helper Text */}
      {images.length === 0 && !currentImage && (
        <p style={{
          marginTop: '15px',
          fontSize: '14px',
          color: 'var(--text-secondary)',
          textAlign: 'center'
        }}>
          No images uploaded yet. Upload your first image above.
        </p>
      )}

      {/* Image Preview Modal */}
      <FilePreviewModalSimple
        file={previewImage}
        isOpen={!!previewImage}
        onClose={() => setPreviewImage(null)}
      />
    </div>
  );
};

export default LabeledImageUpload;
